import { useState } from 'react'

import {
  Menu,
  MenuButton,
  IconButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react'
import { generatePath, Link } from 'react-router'
// eslint-disable-next-line no-restricted-imports -- 新しいアイコンに移行する
import { Expand3 } from 'src/components/__legacy__/icon/monochrome'
import { Paths } from 'src/constants/paths'
import {
  useAuthenticatedAccount,
  usePermission,
} from 'src/features/auth/context'
import { colors } from 'src/lib/chakra-theme/foundations/colors'
import { PERMISSIONS } from 'src/lib/permission'

import {
  ExplanationDetail,
  ExplanationDocRevisionAgreementForm,
} from '../../types'
import { canStartVisit } from '../../utils/canStartVisit'
import { isEditableStatus } from '../../utils/isEditableStatus'
import { isPartner } from '../../utils/isPartner'
import { willStartSession } from '../../utils/willStartSession'
import { DisableExplanationRevisionModal } from '../DisableExplanationRevisionModal/DisableExplanationRevisionModal'
import { RejectAgreementModalWithSelect } from '../RejectAgreementModalWithSelect/RejectAgreementModalWithSelect'
import { ResendReservationEmailModal } from '../ResendReservationEmailModal/ResendReservationEmailModal'
import { VideoCallHistoryModal } from '../VideoCallHistoryModal/VideoCallHistoryModal'
import { WithdrawModal } from '../WithdrawModal/WithdrawModal'

type Props = {
  explanation: ExplanationDetail
  mutateExplanation: () => void
}

export const ExplanationMenu: React.FC<Props> = ({
  explanation,
  mutateExplanation,
}) => {
  const {
    account: {
      selectedTrial,
      selectedTrialHospitalUid,
      selectedTrialHospitalRole,
    },
  } = useAuthenticatedAccount()

  const { hasPermission } = usePermission()

  const { trialPatient } = explanation

  const [modalToOpen, setModalToOpen] = useState<
    | 'VideoCallHistory'
    | 'ResendReservationEmail'
    | 'Disable'
    | 'Reject'
    | 'Withdraw'
    | 'None'
  >('None')

  const openModal = (toOpen: typeof modalToOpen) => {
    setModalToOpen(toOpen)
  }

  const closeModal = () => {
    setModalToOpen('None')
  }

  const status = explanation.latestRevision.status

  // 患者が署名or拒否をしていない（患者の署名履歴が存在しない）文書の一覧
  const unsignedDocRevs = explanation.latestRevision.docSets
    .flatMap(docSet => docSet.explanationDocRevisions)
    .filter(
      (docRev): docRev is ExplanationDocRevisionAgreementForm =>
        !!docRev && docRev.type === 'AgreementForm',
    )
    .filter(docRev =>
      docRev.signHistories.every(h => h.signerRole !== 'Patient'),
    )

  const conditions = {
    canEdit:
      hasPermission(PERMISSIONS.Explanation_Edit) && isEditableStatus(status),
    showVideoCallHistory:
      trialPatient.videoCallRoom.userLogs.filter(log => log.scene !== 'ESource')
        .length > 0,
    canPreview: false, // TODO
    canReject:
      hasPermission(PERMISSIONS.Explanation_Edit) &&
      status !== 'Disabled' &&
      unsignedDocRevs.length > 0,
    canDisable:
      hasPermission(PERMISSIONS.Explanation_Edit) &&
      !!selectedTrialHospitalUid &&
      !isPartner(explanation, selectedTrialHospitalUid) &&
      willStartSession(status),
    canWithdraw:
      hasPermission(PERMISSIONS.Explanation_Edit) && status === 'AgreementDone',
    canResendReservationEmail:
      hasPermission(PERMISSIONS.Explanation_Edit) &&
      selectedTrialHospitalRole === 'Main' &&
      canStartVisit(explanation.latestRevision.statusV2) &&
      trialPatient.reservations.filter(
        r => r.timeStatus == 'Upcoming' && r.explanationReservation,
      ).length > 0,
  }

  const canShowMenu = Object.values(conditions).some(Boolean)

  if (!canShowMenu) {
    return null
  }

  return (
    <>
      <Menu>
        <MenuButton
          as={IconButton}
          colorScheme="blue"
          variant="customIconButtonGhost"
          aria-label="explanation-menu"
          icon={<Expand3 color={colors.blue[500]} />}
        />
        <MenuList>
          {conditions.canEdit && (
            <MenuItem
              as={Link}
              to={generatePath(Paths.ExplanationEdit, {
                trialUid: selectedTrial.uid,
                explanationUid: explanation.uid,
              })}
            >
              説明内容編集
            </MenuItem>
          )}
          {conditions.canPreview && <MenuItem>説明ルームプレビュー</MenuItem>}
          {conditions.showVideoCallHistory && (
            <MenuItem onClick={() => openModal('VideoCallHistory')}>
              ビデオ通話履歴
            </MenuItem>
          )}
          {conditions.canResendReservationEmail && (
            <MenuItem onClick={() => openModal('ResendReservationEmail')}>
              面談案内メールを再送
            </MenuItem>
          )}
          {conditions.canReject && (
            <MenuItem color="red.500" onClick={() => openModal('Reject')}>
              同意拒否
            </MenuItem>
          )}
          {conditions.canDisable && (
            <MenuItem color="red.500" onClick={() => openModal('Disable')}>
              説明を無効化
            </MenuItem>
          )}
          {conditions.canWithdraw && (
            <MenuItem color="red.500" onClick={() => openModal('Withdraw')}>
              同意撤回
            </MenuItem>
          )}
        </MenuList>
      </Menu>

      <VideoCallHistoryModal
        isOpen={modalToOpen === 'VideoCallHistory'}
        onClose={closeModal}
        explanationUid={explanation.uid}
      />

      {conditions.canResendReservationEmail && (
        <ResendReservationEmailModal
          isOpen={modalToOpen === 'ResendReservationEmail'}
          trialPatient={trialPatient}
          partnerHospitalName={
            explanation.latestRevision.partnerTrialHospital?.name
          }
          onClose={closeModal}
        />
      )}

      <DisableExplanationRevisionModal
        isOpen={modalToOpen === 'Disable'}
        explanationRevisionUid={explanation.latestRevision.uid}
        candidateId={explanation.patient.candidateId}
        onClose={closeModal}
      />

      <RejectAgreementModalWithSelect
        docRevisions={unsignedDocRevs}
        isOpen={modalToOpen === 'Reject'}
        onClose={closeModal}
        mutateExplanation={mutateExplanation}
      />

      <WithdrawModal
        isOpen={modalToOpen === 'Withdraw'}
        explanationRevisionUid={explanation.latestRevision.uid}
        candidateId={explanation.patient.candidateId}
        onClose={closeModal}
        mutateExplanation={mutateExplanation}
      />
    </>
  )
}
